import type {
	EposData,
	Tab,
	TabInput,
	TabMembershipResponse,
} from "../../types";

/**
 * HA-554 — the tab the till shows while Create Tab is in flight: a tab with no orders yet. Every field a Tab has, so the
 * panel can read it like any other (it reads `orders`), until the server's read replaces it.
 */
export function optimisticTab(
	tab: TabInput,
	createdByUserId: string | null,
	now: string,
): Tab {
	return {
		id: tab.tabId ?? "",
		number: tab.number,
		name: tab.name ?? null,
		section: tab.section,
		covers: tab.covers,
		status: "Open",
		notes: tab.notes,
		createdByUserId,
		createdAt: now,
		updatedAt: now,
		orders: [],
		total: null,
		outstanding: null,
	};
}

/**
 * HA-554 (planning's ruling on #415) — Add to tab and Move answer with the order and each tab they touched. The till
 * merges those into the EPOS data it holds, in place: the order, and each touched tab's orders and figures (a tab the
 * server has closed leaves). Everything else (menus, products, settings) is left as it is, and EPOS is refreshed in the
 * background rather than replaced by the answer.
 */
export function mergeTabMembership(
	old: EposData,
	response: TabMembershipResponse,
): EposData {
	const order = response.order;
	const orders = !order
		? old.orders
		: old.orders.some((o) => o.id === order.id)
			? old.orders.map((o) => (o.id === order.id ? order : o))
			: [order, ...old.orders];

	const touched = new Map(
		response.tabs.map((detail) => [detail.tab.id, detail]),
	);
	const activeTabs = old.activeTabs
		.filter((tab) => touched.get(tab.id)?.tab.status !== "Closed")
		.map((tab) => {
			const detail = touched.get(tab.id);
			return detail
				? {
						...tab,
						orders: detail.tab.orderIds,
						total: detail.figures.total,
						outstanding: detail.figures.outstanding,
					}
				: tab;
		});

	return { ...old, orders, activeTabs };
}
